import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { uniqueId } from '@uhg-abyss/web/tools/uniqueId';
import { Breadcrumbs } from '@sde/widgets-core/src/common/Breadcrumbs';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { event } from '@uhg-abyss/web/tools/event';
import { useBreadcrumbs } from '../../../state/breadcrumbs';
import { PageLayout } from '../../PageLayout';
import { ConfirmModal } from './ConfirmModal';
import { styled } from '@uhg-abyss/web/tools/styled';

const TopSectionFlex = styled(Flex, {
  alignItems: 'center',
  height: '30px',
  borderBottom: '1px solid $gray4',
});

const TopSectionSpacing = styled('div', {
  height: 30,

  '@media print': {
    display: 'none',
  },
});

const HeaderOverlayDiv = styled('div', {
  position: 'absolute',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  top: '0px',
  right: '0px',
  bottom: '0px',
  left: '0px',
  zIndex: 1200,
  display: 'none',

  '.header-overlay-active &': {
    display: 'block',
  },
});

const FixedSectionDiv = styled('div', {
  backgroundColor: '$primary2',
  width: '100%',
  position: 'fixed',
  zIndex: '1070',

  '@media print': {
    position: 'static',
  },
});

const ActionSpan = styled('span', {
  marginRight: 20,
  '&:last-child': {
    marginRight: 0,
  },
});

const breadcrumbsStyle = {
  fontSize: 12,
  lineHeight: '34px',
};

export const PageHeaderTop = ({ actions }) => {
  const { breadcrumbs } = useBreadcrumbs();
  const [path, setPath] = useState('/');
  const router = useRouter();
  const { getLocation } = useRouter();
  const appLocation = getLocation();

  const { user } = useUser.state();

  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = () => {
    setModalOpen(false);
    storage.session.set('accountInformationEditMode', 'off');
    storage.session.set('communicationPreferenceEditMode', 'off');
    storage.session.set('trainingPreferenceEditMode', 'off');
    storage.session.set('priorAuthAppealEditMode', 'off');
    storage.session.set('claimAppealEditMode', 'off');
    storage.session.set('pendedTicketEditMode', 'off');
    storage.session.set('pendingClaimsEditMode', 'off');
    storage.session.set('reconsiderationEditMode', 'off');
    storage.session.set('smartEditsEditMode', 'off');
    storage.session.set('priorAuthEditMode', 'off');
    storage.session.set('refferalEditMode', 'off');
    storage.session.set('admissionEditMode', 'off');
    storage.session.set('dischargeEditMode', 'off');
    router.push(path);
  };

  const handleContinue = () => {
    setModalOpen(false);
  };
  const onclickHandler = (url, linkName) => {
    event('PAGE_NAV_CLICK', { linkName });
    setPath(url);
    const confirmModal = storage.session.get('accountInformationEditMode');
    const commPrefConfirmModal = storage.session.get(
      'communicationPreferenceEditMode'
    );
    const trainingPrefConfirmModal = storage.session.get(
      'trainingPreferenceEditMode'
    );
    const smartEditsConfirmModal = storage.session.get('smartEditsEditMode');
    const priorAuthConfirmModal = storage.session.get('priorAuthEditMode');
    const referalConfirmModal = storage.session.get('refferalEditMode');
    const admissionConfirmModal = storage.session.get('admissionEditMode');
    const dischargeConfirmModal = storage.session.get('dischargeEditMode');
    const priorAuthAppealConfirmModal = storage.session.get(
      'priorAuthAppealEditMode'
    );
    const claimAppealConfirmModal = storage.session.get('claimAppealEditMode');
    const pendedTicketConfirmModal = storage.session.get(
      'pendedTicketEditMode'
    );
    const pendingClaimsConfirmModal = storage.session.get(
      'pendingClaimsEditMode'
    );
    const reconsiderationConfirmModal = storage.session.get(
      'reconsiderationEditMode'
    );
    const requiredCommunicationPreferenceEdits = storage.session.get(
      'requiredCommunicationPreferenceEdits'
    );
    if (
      confirmModal === 'on' ||
      commPrefConfirmModal === 'on' ||
      trainingPrefConfirmModal === 'on' ||
      referalConfirmModal === 'on' ||
      priorAuthConfirmModal === 'on' ||
      smartEditsConfirmModal === 'on' ||
      reconsiderationConfirmModal === 'on' ||
      pendingClaimsConfirmModal === 'on' ||
      pendedTicketConfirmModal === 'on' ||
      claimAppealConfirmModal === 'on' ||
      priorAuthAppealConfirmModal === 'on' ||
      admissionConfirmModal === 'on' ||
      dischargeConfirmModal === 'on'
    ) {
      setModalOpen(true);
    } else {
      setModalOpen(false);
      if (requiredCommunicationPreferenceEdits === 'on') {
        storage.session.set('requiredCommunicationPreferenceEdits', 'off');
      }
      router.push(url);
    }
  };
  const [ModalPrompt, setModalPrompt] = useState(false);
  useEffect(() => {
    if (appLocation.pathname.startsWith('/prefcenter')) {
      setModalPrompt(true);
    } else {
      setModalPrompt(false);
    }
  }, [appLocation]);

  return (
    <React.Fragment>
      <ConfirmModal
        isOpen={modalOpen}
        closeModal={closeModal}
        continueModal={handleContinue}
      />
      <FixedSectionDiv>
        <PageLayout>
          <TopSectionFlex>
            <Flex.Content>
              <Breadcrumbs
                customBreadCrumb={ModalPrompt && !user.isTechnicalUser}
                onclickHandler={onclickHandler}
                style={breadcrumbsStyle}
                items={breadcrumbs}
              />
            </Flex.Content>
            <If condition={actions}>
              <For each="action" of={actions}>
                <ActionSpan key={uniqueId()}>{action}</ActionSpan>
              </For>
            </If>
          </TopSectionFlex>
        </PageLayout>
        <HeaderOverlayDiv />
      </FixedSectionDiv>
      <TopSectionSpacing />
    </React.Fragment>
  );
};

PageHeaderTop.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.node),
};

PageHeaderTop.defaultProps = {
  actions: null,
};
